import { useEffect, useRef, useState } from "react";
import { Microphone, Stop, Trash, CheckCircle } from "@phosphor-icons/react";

export default function VoiceRecorder({ onRecorded, onClear, accept = "audio" }) {
  const [recording, setRecording] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState("");
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const start = async () => {
    setError("");
    try {
      const constraints = accept === "video" ? { audio: true, video: true } : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const mime = accept === "video" ? "video/webm" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        onRecorded?.(blob, accept === "video" ? "video" : "audio");
        stream.getTracks().forEach((t) => t.stop());
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      setError("Microphone permission denied or not available.");
    }
  };

  const stop = () => {
    recorderRef.current?.stop();
    clearInterval(timerRef.current);
    setRecording(false);
  };

  const clear = () => {
    setBlobUrl(null);
    setSeconds(0);
    onClear?.();
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  if (blobUrl) {
    return (
      <div className="flex items-center gap-3 border border-[#27272A] p-3 bg-[#0A0A0C]" data-testid="voice-recorded-preview">
        <CheckCircle size={18} weight="fill" style={{ color: "#00E676" }} />
        <div className="flex-1">
          {accept === "video" ? (
            <video src={blobUrl} controls className="w-full max-h-48" />
          ) : (
            <audio src={blobUrl} controls className="w-full" />
          )}
        </div>
        <button onClick={clear} className="btn btn-ghost btn-sm" data-testid="voice-recorder-clear">
          <Trash size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 border border-dashed border-[#27272A] p-4 bg-[#0A0A0C]">
      {recording ? (
        <>
          <button onClick={stop} className="btn btn-primary" data-testid="voice-recorder-stop">
            <Stop size={14} weight="fill" /> Stop
          </button>
          <span className="label-mono text-red-500 pulse">REC · {seconds}s</span>
        </>
      ) : (
        <>
          <button onClick={start} className="btn" data-testid="voice-recorder-start">
            <Microphone size={14} /> Record {accept === "video" ? "video" : "voice note"}
          </button>
          <span className="label-mono">AI will auto-transcribe & summarize</span>
        </>
      )}
      {error && <span className="text-xs text-red-500 ml-auto">{error}</span>}
    </div>
  );
}
